<template>
  <div class="cl-chart" ref="wrapper">
    <div v-if="!hasData" class="cl-chart-empty">No data in this range.</div>

    <svg
      v-else
      class="cl-chart-svg"
      :width="width"
      :height="height"
      role="img"
      :aria-label="ariaLabel"
      @mousemove="onMove"
      @mouseleave="onLeave"
      @mousedown="onDown"
      @mouseup="onUp"
      @dblclick="clearSelection"
    >
      <g class="cl-chart-grid">
        <line
          v-for="tick in yTicks"
          :key="`grid-${tick.value}`"
          :x1="padding.left"
          :x2="width - padding.right"
          :y1="tick.y"
          :y2="tick.y"
        />
        <line
          v-for="tick in xTicks"
          :key="`vgrid-${tick.index}`"
          class="is-vertical"
          :x1="tick.x"
          :x2="tick.x"
          :y1="padding.top"
          :y2="height - padding.bottom"
        />
      </g>

      <g class="cl-chart-labels">
        <text
          v-for="tick in yTicks"
          :key="`ylabel-${tick.value}`"
          :x="padding.left - 8"
          :y="tick.y + 4"
          text-anchor="end"
        >{{ tick.label }}</text>
        <text
          v-for="tick in xTicks"
          :key="`xlabel-${tick.index}`"
          :x="tick.x"
          :y="height - 8"
          text-anchor="middle"
        >{{ tick.label }}</text>
      </g>

      <rect
        v-if="dragRect"
        class="cl-chart-selection"
        :x="dragRect.x"
        :y="padding.top"
        :width="dragRect.width"
        :height="plotHeight"
      />

      <g v-for="layer in layers" :key="layer.key">
        <path
          v-if="layer.area"
          class="cl-chart-area"
          :d="layer.area"
          :style="{ fill: layer.color, opacity: areaOpacity }"
        />
        <path
          v-if="layer.line"
          class="cl-chart-line"
          :d="layer.line"
          :style="{ stroke: layer.color, strokeWidth: layer.width }"
        />
      </g>

      <line
        v-if="cursor"
        class="cl-chart-cursor"
        :x1="cursor.x"
        :x2="cursor.x"
        :y1="padding.top"
        :y2="height - padding.bottom"
      />

      <g v-if="cursor">
        <circle
          v-for="point in cursor.points"
          :key="`dot-${point.key}`"
          :cx="cursor.x"
          :cy="point.y"
          r="3.5"
          :style="{ fill: point.color }"
        />
      </g>
    </svg>

    <div v-if="cursor && hasData" class="cl-chart-tooltip" :style="tooltipStyle">
      <strong>{{ cursor.label }}</strong>
      <span v-for="point in cursor.points" :key="`tip-${point.key}`">
        <i class="cl-chart-swatch" :style="{ background: point.color }"></i>
        <span class="cl-chart-tip-name">{{ point.name }}</span>
        <b>{{ formatValue(point.raw) }}</b>
      </span>
      <span v-if="cursor.hidden" class="cl-chart-tip-more">+{{ cursor.hidden }} more</span>
    </div>
  </div>
</template>

<script>
// Inline SVG chart. The dashboard carries no charting dependency and its content
// security policy keeps third party scripts out, so this draws the plot itself:
// line, area and stacked modes, a time axis, a hover readout and drag to zoom.
//
// Series values arrive already shaped by the panel above (metric, bucket size,
// smoothing, running total); this component only maps numbers to pixels.

const PADDING = { top: 12, right: 14, bottom: 26, left: 58 }

function formatClock(ms) {
  const totalSeconds = Math.max(0, Math.round(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours) return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  return `${minutes}:${String(seconds).padStart(2, '0')}`
}

function formatCompact(value) {
  const number = Number(value) || 0
  const abs = Math.abs(number)
  if (abs >= 1000000000) return `${(number / 1000000000).toFixed(1)}B`
  if (abs >= 1000000) return `${(number / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`
  if (abs >= 1000) return `${(number / 1000).toFixed(abs >= 10000 ? 0 : 1)}K`
  return String(Math.round(number))
}

// A readable axis maximum: 1, 2, 2.5 or 5 times a power of ten.
function niceCeiling(value) {
  if (value <= 0) return 1
  const exponent = Math.floor(Math.log10(value))
  const base = 10 ** exponent
  const normalized = value / base
  const step = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10
  return step * base
}

export default {
  name: 'CombatLogTimeChart',
  props: {
    series: { type: Array, default: () => [] },
    bucketMs: { type: Number, default: 1000 },
    // Bucket index the first value belongs to, so a zoomed range keeps real times.
    startIndex: { type: Number, default: 0 },
    height: { type: Number, default: 320 },
    mode: { type: String, default: 'line' },
    unit: { type: String, default: '/s' },
    highlightKey: { type: String, default: '' },
  },
  emits: ['selectRange'],
  data() {
    return {
      width: 900,
      padding: PADDING,
      cursorIndex: -1,
      dragStart: -1,
      dragEnd: -1,
      dragging: false,
      observer: null,
    }
  },
  computed: {
    bucketCount() {
      return this.series.reduce((max, entry) => Math.max(max, (entry.values || []).length), 0)
    },
    hasData() {
      return this.bucketCount > 0 && this.series.some((entry) => (entry.values || []).some((value) => value > 0))
    },
    plotWidth() {
      return Math.max(10, this.width - this.padding.left - this.padding.right)
    },
    plotHeight() {
      return Math.max(10, this.height - this.padding.top - this.padding.bottom)
    },
    step() {
      return this.plotWidth / Math.max(1, this.bucketCount - 1)
    },
    isStacked() {
      return this.mode === 'stacked'
    },
    areaOpacity() {
      return this.isStacked ? 0.72 : 0.16
    },
    // For stacked mode each series is drawn on top of the running sum below it.
    stackedBands() {
      const bands = []
      const running = new Array(this.bucketCount).fill(0)

      this.series.forEach((entry) => {
        const values = entry.values || []
        const lower = [...running]
        for (let index = 0; index < this.bucketCount; index += 1) {
          running[index] += values[index] || 0
        }
        bands.push({ entry, lower, upper: [...running] })
      })

      return bands
    },
    maxValue() {
      let max = 0
      if (this.isStacked) {
        const bands = this.stackedBands
        const top = bands.length ? bands[bands.length - 1].upper : []
        top.forEach((value) => { if (value > max) max = value })
      } else {
        this.series.forEach((entry) => {
          (entry.values || []).forEach((value) => { if (value > max) max = value })
        })
      }
      return niceCeiling(max || 1)
    },
    layers() {
      if (!this.bucketCount) return []
      const baseY = this.height - this.padding.bottom

      if (this.isStacked) {
        return this.stackedBands.map(({ entry, lower, upper }) => ({
          key: entry.key,
          color: entry.color || 'var(--primary)',
          width: 1,
          line: this.pathFor(upper),
          area: `${this.pathFor(upper)} L ${this.xFor(this.bucketCount - 1).toFixed(1)} ${this.yFor(lower[this.bucketCount - 1] || 0).toFixed(1)} ${this.reversePathFor(lower)} Z`,
        }))
      }

      return this.series.map((entry) => {
        const values = entry.values || []
        const line = this.pathFor(values)
        const dimmed = this.highlightKey && entry.key !== this.highlightKey
        return {
          key: entry.key,
          color: entry.color || 'var(--primary)',
          width: this.highlightKey && entry.key === this.highlightKey ? 2.6 : (dimmed ? 1 : 1.8),
          line,
          area: this.mode === 'area'
            ? `${line} L ${this.xFor(this.bucketCount - 1).toFixed(1)} ${baseY.toFixed(1)} L ${this.xFor(0).toFixed(1)} ${baseY.toFixed(1)} Z`
            : '',
        }
      })
    },
    yTicks() {
      const ticks = []
      const steps = 5
      for (let index = 0; index <= steps; index += 1) {
        const value = (this.maxValue / steps) * index
        ticks.push({ value, y: this.yFor(value), label: formatCompact(value) })
      }
      return ticks
    },
    xTicks() {
      const count = this.bucketCount
      if (count < 2) return []
      const desired = Math.max(2, Math.min(10, Math.floor(this.plotWidth / 90)))
      const every = Math.max(1, Math.round(count / desired))
      const ticks = []
      for (let index = 0; index < count; index += every) {
        ticks.push({
          index,
          x: this.xFor(index),
          label: formatClock((this.startIndex + index) * this.bucketMs),
        })
      }
      return ticks
    },
    cursor() {
      if (this.cursorIndex < 0 || !this.hasData) return null
      const x = this.xFor(this.cursorIndex)

      const all = this.series
        .map((entry) => {
          const raw = (entry.values || [])[this.cursorIndex] || 0
          return {
            key: entry.key,
            name: entry.label,
            color: entry.color || 'var(--primary)',
            raw,
            y: this.yFor(this.stackedTop(entry, this.cursorIndex)),
          }
        })
        .filter((point) => point.raw > 0)
        .sort((left, right) => right.raw - left.raw)

      const from = (this.startIndex + this.cursorIndex) * this.bucketMs
      return {
        x,
        points: all.slice(0, 9),
        hidden: Math.max(0, all.length - 9),
        label: `${formatClock(from)} - ${formatClock(from + this.bucketMs)}`,
      }
    },
    dragRect() {
      if (!this.dragging || this.dragStart < 0) return null
      const from = Math.min(this.dragStart, this.dragEnd)
      const to = Math.max(this.dragStart, this.dragEnd)
      return { x: this.xFor(from), width: Math.max(2, (to - from) * this.step) }
    },
    tooltipStyle() {
      if (!this.cursor) return {}
      const flip = this.cursor.x > this.width * 0.55
      return {
        left: `${flip ? this.cursor.x - 14 : this.cursor.x + 14}px`,
        transform: flip ? 'translateX(-100%)' : 'none',
      }
    },
    ariaLabel() {
      return `Time chart, ${this.series.length} series over ${formatClock(this.bucketCount * this.bucketMs)}`
    },
  },
  mounted() {
    this.measure()
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(() => this.measure())
      this.observer.observe(this.$refs.wrapper)
    } else {
      window.addEventListener('resize', this.measure)
    }
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect()
    else window.removeEventListener('resize', this.measure)
  },
  methods: {
    measure() {
      const element = this.$refs.wrapper
      if (!element) return
      this.width = Math.max(320, Math.floor(element.clientWidth))
    },
    xFor(index) {
      return this.padding.left + index * this.step
    },
    yFor(value) {
      const ratio = Math.min(1, Math.max(0, value / this.maxValue))
      return this.padding.top + this.plotHeight - ratio * this.plotHeight
    },
    stackedTop(entry, index) {
      if (!this.isStacked) return (entry.values || [])[index] || 0
      const band = this.stackedBands.find((item) => item.entry.key === entry.key)
      return band ? band.upper[index] || 0 : 0
    },
    pathFor(values) {
      let path = ''
      for (let index = 0; index < this.bucketCount; index += 1) {
        const x = this.xFor(index).toFixed(1)
        const y = this.yFor(values[index] || 0).toFixed(1)
        path += `${index === 0 ? 'M' : ' L'} ${x} ${y}`
      }
      return path
    },
    reversePathFor(values) {
      let path = ''
      for (let index = this.bucketCount - 1; index >= 0; index -= 1) {
        path += ` L ${this.xFor(index).toFixed(1)} ${this.yFor(values[index] || 0).toFixed(1)}`
      }
      return path
    },
    indexFromEvent(event) {
      const bounds = event.currentTarget.getBoundingClientRect()
      const offsetX = event.clientX - bounds.left - this.padding.left
      return Math.max(0, Math.min(this.bucketCount - 1, Math.round(offsetX / this.step)))
    },
    onMove(event) {
      this.cursorIndex = this.indexFromEvent(event)
      if (this.dragging) this.dragEnd = this.cursorIndex
    },
    onLeave() {
      this.cursorIndex = -1
      if (this.dragging) this.finishDrag()
    },
    onDown(event) {
      this.dragging = true
      this.dragStart = this.indexFromEvent(event)
      this.dragEnd = this.dragStart
    },
    onUp() {
      if (this.dragging) this.finishDrag()
    },
    finishDrag() {
      this.dragging = false
      const from = Math.min(this.dragStart, this.dragEnd)
      const to = Math.max(this.dragStart, this.dragEnd)
      this.dragStart = -1
      this.dragEnd = -1
      if (to - from < 1) return
      this.$emit('selectRange', { from: this.startIndex + from, to: this.startIndex + to })
    },
    clearSelection() {
      this.$emit('selectRange', null)
    },
    formatValue(value) {
      return `${formatCompact(value)}${this.unit}`
    },
  },
}
</script>
